<template>
  <el-dialog
    :title="generateTitle"
    v-model="dialogModel"
    width="90%"
    :show-close="false"
    :close-on-press-escape="false"
    :close-on-click-modal="false">
    <el-row type="flex" align="middle" style="margin-bottom: 12px;">
      <el-col :span="12">
        <el-row type="flex" align="middle" justify="start" style="margin-bottom: 12px;">
          <div style="display: inline-flex; width: 10px; height: 10px;" class="cell-new"> </div>
          <div style="display: inline-flex; margin-left: 6px">New value</div>
          <div style="display: inline-flex; width: 10px; height: 10px; margin-left: 24px;" class="cell-original"> </div>
          <div style="display: inline-flex; margin-left: 6px">Previous value</div>
        </el-row>
      </el-col>
      <el-col :span="12">
        <el-row type="flex" align="middle" justify="end">
          <el-button size="small" @click="viewDcJe">View Journal Entry</el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row>
      <el-col>
        <el-row type="flex" align="middle" justify="center">
          <div style="width: 100%;">
            <table class="dc-table" style="width: 100%;">
              <colgroup>
                <template v-for="field in lineFieldsFiltered"><col v-if="!['company', 'production'].includes(field.name) && !field.hidden" :key="field.name" :style="setStyle(field.name)"></template>
              </colgroup>
              <tbody>
              <tr>
                <template v-for="field in lineFieldsFiltered"><th v-if="!['company', 'production'].includes(field.name) && !field.hidden" :key="field.name">
                  {{ field.label }}
                </th></template>
              </tr>
              <tr v-for="(dc, dcIndex) in dcWithDiff" :key="dcIndex" class="dc-row">
                <td>
                  <el-icon v-if="dc.isSplittingLine && dc.line_number === currentDc.line_number"><CaretRight/></el-icon>
                  {{ dc.original ? `${dc.line_number} (original)` : dc.line_number }}
                </td>
                <td
                  v-for="field in lineFieldsFiltered.filter(ff => !['line_number', 'company', 'production'].includes(ff.name) && !ff.hidden)"
                  :key="field.name"
                  :class="getCellClass(dc, field.name)">
                  <div v-if="field.name === 'asset'" style="text-align: center;">
                    <el-checkbox :value="getFormattedFieldValue(dc.form[field.name])" disabled />
                  </div>
                  <div v-else :style="`text-align: ${field.name === 'amount_decimal' ? 'right' : 'left'}`">
                    {{ field.name === 'amount_decimal' ? formatAmountLocale(getFormattedFieldValue(dc.form[field.name])) : getFormattedFieldValue(dc.form[field.name]) }}
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle" style="padding: 20px 0 0 0;">
      <el-button v-if="viewableDcs.length > 1" :disabled="currentIndex <= 0" @click="viewPrev">
        Previous
      </el-button>
      <el-button v-if="viewableDcs.length > 1" :disabled="currentIndex >= viewableDcs.length - 1" @click="viewNext">
        Next
      </el-button>
      <el-button type="primary" @click="close">
        Close
      </el-button>
    </el-row>
  </el-dialog>
</template>

<script>
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
  import { mapGetters } from 'vuex'
  import { formatAmount } from '@/utils/helpers/AmountFormatter'

  export default {
    props: {
      viewedDcId: {
        default: () => null,
        type: Number
      },
      viewableDcs: {
        default: () => [],
        type: Array
      },
      lineFields: {
        default: () => [],
        type: Array
      },
      trxId: {
        default: () => null,
        type: Number
      },
      trxType: {
        default: () => null,
        type: String
      },
      showOrClose: {
        default: () => null,
        type: Function
      }
    },
    data () {
      return {
        currentIndex: 0
      }
    },
    methods: {
      formatAmountLocale (amount) {
        return formatAmount(amount)
      },
      /**
       * Shows the next DC
       */
      viewNext () {
        this.currentIndex++
      },
      /**
       * Shows the previous DC
       */
      viewPrev () {
        this.currentIndex--
      },
      /**
       * Closes the modal
       */
      close () {
        this.showOrClose(null)
        this.currentIndex = 0
      },
      /**
       * Shows the DC JE corresponding to this DC specified in this.viewedDcModel
       */
      viewDcJe () {
        if (this.viewedDcModels[0] && this.viewedDcModels[0].form.transaction_id) {
          window.open(`/jes/${this.viewedDcModels[0].form.transaction_id}`, '_blank')
        }
      },
      /**
       * Returns the class name that should be assigned to the <td> element for the field name provided in
       * the fieldName argument. Possible class names are 'cell-original' or 'cell-new', which
       * is determined by checking whether the field's value was changed in a DC.
       * @param dc
       * @param fieldName
       */
      getCellClass (dc, fieldName) {
        if (!this.deltas.hasOwnProperty(fieldName)) {
          return null // field was not changed
        }
        return dc.original ? 'cell-original' : 'cell-new'
      },
      /**
       * Because typeahead autocomplete fields must have 'label' and 'value' properties, extracts the actual
       * label by checking whether it has a 'label' property.
       */
      getFormattedFieldValue (field) {
        if (field && field.hasOwnProperty('label')) {
          return field.label
        }
        return field
      },
      /**
       * Returns field-specific styles.
       * @param field   The field name
       */
      setStyle (field) {
        let style = { 'text-align': 'left' }
        switch (field) {
          case 'line_number':
            style['width'] = '3%'
            style['text-align'] = 'center'
            break
          case 'relieves':
            style['width'] = '10%'
            break
          case 'amount_decimal':
            style['width'] = '8%'
            break
          case 'account':
          case 'location':
          case 'series':
          case 'set':
          case 'tax_code':
          case 'free_field_1':
          case 'free_field_2':
          case 'free_field_3':
          case 'free_field_4':
            style['width'] = '4%'
            break
          case 'asset':
            style['width'] = '3%'
            style['text-align'] = 'center'
            break
          case 'third_party_vendor':
          case 'other_name':
            style['width'] = '6%'
            break
        }
        return style
      },
      /**
       * Sets the label and value properties on typeahead/autocomplete fields. This is necessary because these types
       * of fields must display a value that is different than their actual value (i.e. display a code, but the
       * actual value is an id).
       * @param form
       */
      setFormLabelsAndValues (form) {
        const { getters } = this.$store
        const ret = {}
        for (let field in form) {
          const getter = () => getters[`get${capitalizeFirstLetter(field.replace(/[^A-Za-z]+/g, ''))}ById`]
          const id = form[field]
          switch (field) {
            case 'relieves':
              ret[field] = Object.values(getters.getPoLineById(id)) && getters.getPoLineById(id).id
                ? { label: getters.getPoLineById(id).meta_name, value: id }
                : { label: '', value: '' }
              break
            case 'account':
              if (form['account_code']) {
                ret[field] = { label: form['account_code'], value: id }
              } else {
                ret[field] = Object.values(getter()(id)) && getter()(id).code
                  ? { label: getter()(id).code, value: id }
                  : { label: '', value: '' }
              }
              break
            case 'series':
            case 'location':
            case 'set':
            case 'tax_code':
            case 'free_field_1':
            case 'free_field_2':
            case 'free_field_3':
            case 'free_field_4':
              ret[field] = Object.values(getter()(id)) && getter()(id).code
                ? { label: getter()(id).code, value: id }
                : { label: '', value: '' }
              break
            case 'third_party_vendor':
              ret[field] = Object.values(getters.getVendorById(id)) && getters.getVendorById(id).code
                ? { label: getters.getVendorById(id).code, value: id }
                : { label: '', value: '' }
              break
            default:
              ret[field] = id
          }
        }
        return ret
      }
    },
    computed: {
      dialogModel: {
        get () { return !!this.viewedDcId },
        set (v) { if (!v) this.close() }
      },
      ...mapGetters({
        getLineById: 'getLineById'
      }),
      /**
       * Returns the lineFields prop value without display-only fields such as 'account:name'
       */
      lineFieldsFiltered () {
        return this.lineFields.filter(field => field.name !== 'account:name')
      },
      /**
       * Returns the DC line currently being viewed
       */
      viewedDcModels () {
        return this.currentDc ? this.viewableDcs.filter(dc => dc.form.original_line_id === this.currentDc.form.original_line_id) : []
      },
      /**
       * Generates the modal title
       */
      generateTitle () {
        if (this.viewedDcModels && this.viewedDcModels.length === 1) {
          return `Distribution Change for Line ${this.viewedDcModels[0].line_number}`
        } else {
          if (this.dcWithDiff[this.dcWithDiff.length - 1]) {
            const splitLine = this.dcWithDiff[this.dcWithDiff.length - 1].form.line_number
            return `Distribution Change: Line ${this.currentDc.line_number} (splits original line #${splitLine})`
          }
        }
      },
      /**
       * Returns all DCs with original lines inserted
       * @return {[]}
       */
      dcWithDiff () {
        if (!this.viewedDcModels.length) {
          return []
        }
        const viewedDcs = this.viewedDcModels.map(dc => {
          return { ...dc, form: this.setFormLabelsAndValues(dc.form) }
        })
        let dc = [...viewedDcs]
        const original = this.getLineById(capitalizeFirstLetter(this.trxType), viewedDcs[0].form.original_line_id) // this should give you the original, unaltered line
        if (original && original.id) {
          const clone = { ...original }
          dc.push({ form: this.setFormLabelsAndValues(clone), line_number: original.line_number, original: true })
        }
        return dc
      },
      /**
       * Gets the changes between both lines
       */
      deltas () {
        const deltas = {}
        if (!(this.dcWithDiff[0] && this.dcWithDiff[1])) {
          return deltas
        }
        const getValue = (form, field) => form[field] && form[field].value ? form[field].value : form[field]
        for (let field in this.dcWithDiff[0].form) {
          if (field === 'id' || field === 'original_line_id' || field === 'transaction_id') {
            continue
          }
          const dcValue = getValue(this.dcWithDiff[0].form, field)
          const origValue = getValue(this.dcWithDiff[1].form, field)
          if (JSON.stringify(dcValue) !== JSON.stringify(origValue)) { // Need to stringify because comparing two identical objects for equality will yield false
            deltas[field] = dcValue
          }
        }
        return deltas
      },
      /**
       * Returns the DC currently being viewed
       * @return {*}
       */
      currentDc () {
        return this.viewableDcs[this.currentIndex]
      }
    },
    watch: {
      viewedDcId () {
        this.currentIndex = this.viewableDcs.findIndex(dc => dc.form.id === this.viewedDcId)
      }
    }
  }
</script>

<style scoped>
  table, th, td, tr {
    border-collapse: collapse;
    border: 1px solid #dfe6ec;
  }
  th {
    padding: 6px;
    background-color: #eef1f6;
  }
  td {
    max-width: 100px;
    padding: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .dc-row {
    background: #ffffff;
  }
  .cell-original {
    background: #fcbdbd;
  }
  .cell-new {
    background: #bfffbf;
  }
</style>
